/**
 * The trust section, filled with architecture instead of badges.
 *
 * Every competitor surveyed pitches privacy as policy — certifications,
 * "we don't train on your data" — while the audio still leaves the device.
 * The free app can make the claim none of them can, so the section shows its
 * pipeline inside a phone outline: recording, transcription, linking, each
 * with a check, and nothing leaving.
 *
 * Every absolute here is scoped to the free tier on purpose (61e9, FR-028):
 * premium note cleanup sends transcript text — never audio — to Sparkie's
 * service, so the old unqualified "your notes never leave the phone" and
 * "there is no Sparkie server" would now be lies. The lede scopes the claim,
 * the paragraph beside the phone names the one paid exception, and the
 * closing rule is the fence that keeps both honest: nothing that touches the
 * network turns itself on. That sentence also fences the "Later" section
 * further down. The pipeline lost its "Titles & tags" stage in the same
 * change — naming moved into the paid cleanup, and free notes carry none.
 *
 * The pills and the device panel are information, not controls, so their
 * boundaries are drawn with `outlineVariant`/`outline` per the exclusions
 * `contrast.test.ts` documents; all text sits in audited roles.
 */

import { StyleSheet, Text as RawText, View } from "react-native";
import { Text } from "react-native-paper";
import { MarketingSection } from "@/components/marketing/section";
import { SectionHead } from "@/components/marketing/section-head";
import { useAppTheme, type AppTheme } from "@/theme/paper-theme";
import { radius, spacing } from "@/theme/tokens";
import { CHIP_RADIUS } from "@/theme/web-tokens";

const LEDE =
  "Most apps promise not to look at your notes. On the free tier, Sparkie can't look — your notes never leave the phone.";

const STAGES = ["Recording", "Transcription", "Linking"] as const;

// Each pill must be true on every tier with no scoping needed: no account is
// NEEDED (the free tier runs without one; premium's optional account is the
// policy page's story), audio never leaves the phone on any tier (premium
// cleanup sends transcript text only), and nothing in the app ever requires
// signal — premium's queued cleanup waits for the network rather than asking
// for it.
const PILLS = [
  "No account needed",
  "Audio never leaves",
  "No signal needed",
] as const;

const CLAIM =
  "The listening, the writing and the linking all happen on the phone itself. Premium note cleanup is the one exception: it sends transcript text — never audio — to Sparkie's service, where it's tidied, handed straight back, and kept nowhere.";

const RULE =
  "If a feature ever needs the network, it stays off until you turn it on.";

function DevicePipeline({ theme }: { theme: AppTheme }) {
  return (
    <View
      style={[
        styles.device,
        {
          backgroundColor: theme.colors.elevation.level1,
          borderColor: theme.colors.outline,
        },
      ]}
    >
      <RawText
        style={[styles.deviceLabel, { color: theme.colors.onSurfaceVariant }]}
      >
        YOUR IPHONE
      </RawText>

      {STAGES.map((stage, index) => (
        <View key={stage} style={styles.stageGroup}>
          {index > 0 ? (
            // Flow decoration for sighted readers; the stage order carries
            // the meaning, so assistive tech skips it.
            <RawText
              aria-hidden
              style={[styles.arrow, { color: theme.colors.onSurfaceVariant }]}
            >
              ↓
            </RawText>
          ) : null}
          <View
            style={[
              styles.stageRow,
              {
                backgroundColor: theme.colors.background,
                borderColor: theme.colors.outlineVariant,
              },
            ]}
          >
            <RawText
              aria-hidden
              style={[styles.tick, { color: theme.status.success }]}
            >
              ✓
            </RawText>
            <RawText
              style={[styles.stageText, { color: theme.colors.onSurface }]}
            >
              {stage}
            </RawText>
          </View>
        </View>
      ))}
    </View>
  );
}

export function Privacy() {
  const theme = useAppTheme();

  return (
    <MarketingSection style={styles.section}>
      <SectionHead
        eyebrow="Privacy"
        heading="Out of the box, everything happens on your iPhone."
        lede={LEDE}
      />

      <View style={styles.stage}>
        <DevicePipeline theme={theme} />

        <View style={styles.claims}>
          <View style={styles.pillRow}>
            {PILLS.map((pill) => (
              <View
                key={pill}
                style={[styles.pill, { borderColor: theme.colors.outline }]}
              >
                <RawText
                  style={[styles.pillText, { color: theme.colors.onSurface }]}
                >
                  {pill}
                </RawText>
              </View>
            ))}
          </View>

          <Text
            variant="bodyLarge"
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            {CLAIM}
          </Text>

          <Text
            variant="bodyLarge"
            style={[styles.rule, { color: theme.colors.onSurface }]}
          >
            {RULE}
          </Text>
        </View>
      </View>
    </MarketingSection>
  );
}

/** The device rail's shell rounding — bespoke geometry, not a token step. */
const DEVICE_RADIUS = 36;

const styles = StyleSheet.create({
  section: {
    gap: spacing.xl,
  },
  stage: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.xxl,
    alignItems: "center",
    justifyContent: "center",
  },
  device: {
    width: 300,
    maxWidth: "100%",
    borderWidth: 1.5,
    borderRadius: DEVICE_RADIUS,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
    gap: spacing.xs,
  },
  deviceLabel: {
    fontSize: 11,
    fontWeight: "600",
    letterSpacing: 1.5,
    textAlign: "center",
    marginBottom: spacing.xxs,
  },
  stageGroup: {
    gap: spacing.xs,
  },
  arrow: {
    fontSize: 13,
    textAlign: "center",
  },
  stageRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    borderWidth: 1,
    borderRadius: CHIP_RADIUS,
    paddingVertical: spacing.xs + 2,
    paddingHorizontal: spacing.md,
  },
  tick: {
    fontSize: 14,
    fontWeight: "700",
  },
  stageText: {
    fontSize: 14.5,
    fontWeight: "500",
  },
  claims: {
    maxWidth: 380,
    width: "100%",
    gap: spacing.md,
  },
  pillRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.xs,
  },
  pill: {
    borderWidth: 1,
    borderRadius: radius.full,
    paddingVertical: spacing.xs - 1,
    paddingHorizontal: spacing.md,
  },
  pillText: {
    fontSize: 14,
    fontWeight: "600",
  },
  rule: {
    fontWeight: "500",
  },
});
